/**
 * `WorkspacePinButton` — the generic pin/unpin affordance mounted into each
 * pinnable tab surface (Table Explorer / notebook / query toolbars, the Schema
 * Compare header and the Data Compare header). It renders purely from
 * `useWorkspacePinState` and owns the create / unpin / update mutations.
 *
 * Chrome: a GHOST, ICON-ONLY button matching the sibling icon actions on every
 * host toolbar (`variant="ghost"` + `h-7 px-2` + a 16px glyph — the same recipe
 * as the query toolbar's history/AI-chat buttons and the Table Explorer's own
 * icon actions). It used to be an outline button with a visible "Pin"/"Pinned"
 * label (R3-8), which made it the odd control out: the only bordered, labelled
 * item in a row of borderless icons, and 32px tall where its neighbours are
 * 28px — which also forced those toolbars' rows taller. State is carried by the
 * glyph and the tooltip, like every other icon button on the bar.
 *
 * Icon language (per Task 18 brief — NOT unified with the tab-strip icon map):
 *  - unpinned-eligible → outline `Pin`, tooltip "Pin this workspace" (click
 *                        pins directly, no menu).
 *  - linked            → filled `Pin`, tooltip "Pinned: <name>" (menu: Pin as
 *                        new / Unpin).
 *  - linked + diverged → filled `Pin` + a 1.5px amber dot, tooltip "(edited)";
 *                        menu adds Update.
 *
 * Colour note — the glyph inherits the button's foreground and is deliberately
 * NOT orange. `src/index.css` reserves orange for "work in flight" and forbids
 * it on idle-persistent chrome, and a pin is idle-persistent.
 *
 * `disabledReason` lets a host toolbar layer surface-specific ineligibility
 * (e.g. blueprint-edit / row-context Table Explorer, Task 19) ON TOP of the
 * hook's generic F12 rule. Any disabled reason becomes the tooltip (repo rule:
 * every disabled control explains WHY). The button carries `aria-disabled`
 * rather than `disabled` on purpose — `disabled:pointer-events-none` would
 * swallow the hover that reveals that very tooltip.
 */

import { useState } from 'react';
import { Pin } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { useWorkspacePinState } from '../hooks/useWorkspacePinState';
import { useWorkspacePinActions } from '../hooks/useWorkspacePinActions';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../components/ui/dropdown-menu';

/** Matches the 16px glyph every sibling toolbar icon button uses. */
const GLYPH_CLASS = 'h-4 w-4';

function PinGlyph({ filled, diverged }: { filled: boolean; diverged?: boolean }) {
  return (
    <span className="relative inline-flex">
      <Pin className={GLYPH_CLASS} fill={filled ? 'currentColor' : 'none'} />
      {diverged && (
        <span
          data-testid="workspace-pin-diverged-dot"
          className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-amber-500 ring-1 ring-background"
        />
      )}
    </span>
  );
}

/**
 * `ml-auto` is inherited from the pre-R3-8 icon and deliberately kept: the
 * Table Explorer toolbar relies on it to push the pin right, and it is inert
 * on the hosts that already spacer it into place (Schema Compare header, Data
 * Compare header, query/notebook toolbars).
 */
const TRIGGER_CLASS = 'ml-auto shrink-0 h-7 px-2';

interface WorkspacePinButtonProps {
  tabId: string;
  /** Surface-specific ineligibility supplied by the host toolbar (Task 19). */
  disabledReason?: string;
}

export function WorkspacePinButton({ tabId, disabledReason }: WorkspacePinButtonProps) {
  const state = useWorkspacePinState(tabId);
  const { createPin, updatePin, unpin } = useWorkspacePinActions(tabId);
  const [menuOpen, setMenuOpen] = useState(false);

  const effectiveDisabledReason =
    disabledReason ?? (state.mode === 'disabled' ? state.reason : undefined);

  if (effectiveDisabledReason) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-disabled="true"
        title={effectiveDisabledReason}
        // Icon-only, so the accessible name has to say "Pin" itself.
        aria-label={`Pin — unavailable: ${effectiveDisabledReason}`}
        data-testid="workspace-pin-disabled"
        className={`${TRIGGER_CLASS} opacity-40 cursor-not-allowed`}
      >
        <PinGlyph filled={false} />
      </Button>
    );
  }

  if (state.mode === 'unpinned') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={createPin}
        title="Pin this workspace"
        aria-label="Pin this workspace"
        data-testid="workspace-pin-unpinned"
        className={TRIGGER_CLASS}
      >
        <PinGlyph filled={false} />
      </Button>
    );
  }

  // linked (optionally diverged) — disabled + unpinned already returned above.
  if (state.mode !== 'linked') return null;
  const { pin, diverged } = state;
  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // Icon-only: the tooltip is the only place the pinned state and the
          // pin's name are spelled out.
          title={diverged ? `Pinned: ${pin.name} (edited)` : `Pinned: ${pin.name}`}
          aria-label={`Pinned workspace: ${pin.name}`}
          data-testid={diverged ? 'workspace-pin-diverged' : 'workspace-pin-linked'}
          className={TRIGGER_CLASS}
        >
          <PinGlyph filled diverged={diverged} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {diverged && (
          <DropdownMenuItem onSelect={() => updatePin(pin)}>
            Update &lsquo;{pin.name}&rsquo;
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={createPin}>Pin as new</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => unpin(pin)}>Unpin</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
